import type { ShrimpState, Slope, World } from '../types';
import type { SiteId } from '../data/sites';
import { DEEP7, fishingYearStart, stepDeep7 } from '../bottomfish';
import { STREAM, stockStream } from '../streams';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';
import { SHRIMP_FISHING, stepShrimp } from '../shrimp';
import { SHRIMP_RECRUITS_PER_DAY, SHRIMP_TABLE } from '../data/shrimp/mauiNui';
import { held } from '../held';

/** Whether a site has a deep slope's stocks: Maui Nui, of the main Hawaiian Islands (the Deep 7's assessment's, the shrimp's survey's). */
export const siteHasSlope = (siteId: SiteId): boolean => siteId === 'maui_nui';

/** The Deep 7's process error on a day, a standard normal on their stock's stream of the day (one stock, a draw only on 1 July). */
export function deep7Normal(world: { seed: number; day: number }, siteId: SiteId, streamOf: typeof stockStream = stockStream): () => number {
  return () => streamOf(world, STREAM.deep7, siteId, 0).normal();
}

/**
 * Step 3 of the tick, the animals, after the monk seals (M192): each sanctuary's Deep 7 on their own stream of the day
 * (a test hands in its own as `streamOf`), then its deepwater shrimp (M193), which draw nothing.
 */
export function stepSlope(world: World, streamOf: typeof stockStream = stockStream): void {
  const epochDay = epochDayOf(world);
  for (const s of world.sanctuaries) if (s.slope) stepSlopeDay(s.slope, epochDay, deep7Normal(world, s.siteId, streamOf));
}

/** A deep slope's day: its Deep 7, their year's error `normal` (the stream's, or 0), then its shrimp. The tick and a save's migration both run it (review M194: each had its own copy). */
export function stepSlopeDay(slope: Slope, epochDay: number, normal: () => number): void {
  stepDeep7(slope.deep7, epochDay, normal);
  stepShrimpDay(slope.shrimp, epochDay);
}

/** A day of the shrimp at their table's recruits and today's fishing. */
export function stepShrimpDay(shrimp: ShrimpState, epochDay: number): void {
  stepShrimp(shrimp, epochDay, SHRIMP_RECRUITS_PER_DAY, SHRIMP_FISHING);
}

/**
 * The deepwater shrimp as a world opens on a day (M193): the table (`data/shrimp/`, the stock settled at today's
 * fishing on a year's last day), stepped from the opening year's first of January to the day (no draw: the shrimp take
 * none).
 */
export function openingShrimp(epochDay: number): ShrimpState {
  const shrimp: ShrimpState = { females: [...SHRIMP_TABLE.females], males: [...SHRIMP_TABLE.males] };
  for (let d = epochDayFromCivil(civilFromEpochDay(epochDay).year, 1, 1); d <= epochDay; d++) stepShrimpDay(shrimp, d);
  return shrimp;
}

/**
 * The deep slope as a world opens on a day: its shrimp (`openingShrimp`), and its Deep 7, the assessment's biomass at
 * the start of fishing year 2023 (`DEEP7.b2023Kg`), turned year by year by the game's own step at no process error to
 * the start of the opening day's fishing year (no draw: a world's opening draws nothing). A world opening before fishing year 2023 takes the 2023 biomass as
 * its own year's start (open: the assessment's earlier years are not carried).
 */
export function openingSlope(epochDay: number): Slope {
  const target = fishingYearStart(epochDay);
  let start = epochDayFromCivil(...DEEP7.from2023);
  const deep7 = { yearStartKg: DEEP7.b2023Kg };
  while (start < target) {
    start = fishingYearStart(start + 366);
    stepDeep7(deep7, start, () => 0);
  }
  return { deep7: { yearStartKg: held(deep7.yearStartKg) }, shrimp: openingShrimp(epochDay) };
}

/** A sanctuary's deep slope as a world opens on a day, where its site has one (`siteHasSlope`); elsewhere none. */
export function sanctuaryOpeningSlope(siteId: SiteId, epochDay: number): Slope | null {
  return siteHasSlope(siteId) ? openingSlope(epochDay) : null;
}
